import React, { useState } from "react";
import { connect } from "react-redux";
import { withLocalize } from "react-localize-redux";
import {
  CardContainer,
  CardContent,
  BgContainer,
  DetailContainer,
  Title,
  Text,
  PriceQuantityRow,
  Price,
  SelectContainer,
  TextAvailable,
  CheckboxContainer,
  ImageIcon,
  OptionTextContainer,
  ViewFullDesc,
  HideFullDescContainer,
  DependencyDisabledOverlay,
} from "./OptionCardStyles";
import Quantity from "../inputs/Quantity";
import CheckboxInput from "../inputs/CheckboxInput";
import ImageComponent from "../images/ImageComponent";
import Alert from "../alert/Alert";
import { CostTypes, checkOption } from "../../infra/services/utils/Options";
import EnlargeIcon from "../../components/svg/Enlarge";
import ImageModal from "../../components/imageModal/ImageModal";
import truncate from "truncate-html";
import ArrowLeft from "../svg/ArrowLeft";
import RoundButton from "../../components/buttons/RoundButton";
import { isString } from "./OptionUtils";
import { Icon, Tooltip, TimePicker } from "antd";
import moment from "moment";
import styledComponents from "styled-components";
import { PrimaryColour } from "../../styles/Colours";

const textLimit = 40;

const PriceWithWarning = styledComponents.div`
  display: flex;
  align-content: center;
  align-items: center;

  .anticon { 
    color: ${({ premium }) => premium ? PrimaryColour : '#fb635b'};
    margin-right: 5px;
    font-size: ${p => p?.inModal ? '18px' : '20px'} ;
  }
`;

const OptionCard = ({
  question,
  record,
  canEditSection,
  isProcessClosed,
  input,
  wedding,
  translate,
  activeLanguage,
  index,
  inModal = false,
  graphicsLocked = false,
  allSelectedOptionIds = new Set(),
  questionOptionsMap = {}
}) => {
  const selected = input?.value && Array.isArray(input.value) && input.value?.find((x) => x.id === record._id);
  const isPremiumOption = wedding && wedding.upgrade && wedding.upgrade.hasPremiumOptions && record && record?.isPremiumOption ? true : false;
  const isTrendOption = wedding && wedding.upgrade && wedding.upgrade.hasPremiumOptions && record && record?.isTrendOption ? true : false;
  const isPremiumAndRoyalOption = wedding && wedding.upgrade && wedding.upgrade.hasPremiumOptions && record && record?.isPremiumAndRoyalOption ? true : false;

  const isPremiumOptionOption = record && record?.isPremiumOption ? true : false;
  const isTrendOptionOption = record && record?.isTrendOption ? true : false;
  const isPremiumAndRoyalOptionOption = record && record?.isPremiumAndRoyalOption ? true : false;

  const showUpgradeIcon = (record && (record?.isTrendFood || record?.isPremiumFood || record?.isPremiumAndRoyalFood || record?.isTrendOption || record?.isPremiumOption || record?.isPremiumAndRoyalOption)) ? true : false;

  // Evaluate DISABLE dependency (combining saved cross-question IDs with current question selections)
  const currentQuestionIds = new Set(
    (Array.isArray(input?.value) ? input.value : []).map(r => r?.id ? String(r.id) : null).filter(Boolean)
  );
  const combinedSelectedIds = new Set([...allSelectedOptionIds, ...currentQuestionIds]);
  const deps = record?.dependencies;
  let dependencyDisabled = false;
  if (deps?.items?.length && deps.effect === 'DISABLE') {
    const grouped = {};
    deps.items.forEach(item => {
      const g = item.group ?? 0;
      if (!grouped[g]) grouped[g] = [];
      grouped[g].push(item);
    });
    const satisfied = Object.entries(grouped).some(([gKey, groupItems]) => {
      const gMode = deps.groups?.[Number(gKey)]?.mode || 'ALL';
      const evaluateItem = (item) => {
        if (item.targetQuestion) {
          const qId = String(item.targetQuestion?._id || item.targetQuestion);
          const qOptionIds = questionOptionsMap[qId];
          if (!qOptionIds) return true;
          const anySelected = Array.from(combinedSelectedIds).some(id => qOptionIds.has(id));
          return item.mustBeSelected ? anySelected : !anySelected;
        }
        const depId = String(item.option?._id || item.option);
        const isSelected = combinedSelectedIds.has(depId);
        return item.mustBeSelected ? isSelected : !isSelected;
      };
      return gMode === 'ALL' ? groupItems.every(evaluateItem) : groupItems.some(evaluateItem);
    });
    if (!satisfied) dependencyDisabled = true;
  }

  let disabled = dependencyDisabled;
  let disabledForCouple = record?.blockForCouple ? true : false;

  const [savedTime, setSavedTime] = useState(null);
  const [showQuantity, setShowQuantity] = useState(selected);
  const [showImageModal, setImageModal] = useState(false);
  const [showFullDesc, setFullDesc] = useState(false);

  let text = record.description ? record.description[activeLanguage?.code] : "";

  if (text?.length > textLimit && !showFullDesc) {
    text = truncate(text, textLimit);
  }

  if (
    question.type === 4 &&
    input.value.length > 0 &&
    !input.value.find((x) => x.id === record._id)
  ) {
    //We have to check every element in input.value
    //If at least one option still exists in this question, then we can disable this option
    //If not then we cannot disable any option as the current option doesn't exist anymore
    for (let i = 0; i < input.value.length; i++) {
      if (checkOption(input.value[i], question, wedding.wedding_place)) {
        disabled = true;
        break;
      }
    }
  }

  const displayQuantity = (value) => {
    setShowQuantity(value);
  };

  const toggleOption = (id) => {
    if (isRoomPlanLocationsQuestion()) {
      return;
    }

    if (graphicsLocked) {
      return Alert.new({
        type: "success",
        title: translate("BLOCKED_ANSWER_TITLE"),
        text: translate("GRAPHICS_BLOCKED_ERROR"),
      });
    }

    if (!disabled && !disabledForCouple) {
      if (isProcessClosed) {
        Alert.new({
          type: "success",
          title: translate("BLOCKED_ANSWER_TITLE"),
          text: translate("PROCESS_CLOSED_TEXT"),
        });
      } else if (canEditSection) {
        const list = [...input.value];
        const index = list.findIndex((x) => x.id === id);

        if (index >= 0) {
          if (list[index].time) setSavedTime(list[index].time);
          list.splice(index, 1);
          displayQuantity(false);
        } else {
          const timeToRestore = savedTime ? { time: savedTime } : {};
          if (wedding?.upgrade?.hasPremiumOptions && (record?.isPremiumOption || record?.isTrendOption || record?.isPremiumAndRoyalOption)) {
            if (wedding?.upgrade?.tag === 'PRIME' && record?.isPremiumOption) {
              list.push({ id: id, qtd: 1, cost: 0, ...timeToRestore });
            } else if (wedding?.upgrade?.tag === 'TREND' && record?.isTrendOption) {
              list.push({ id: id, qtd: 1, cost: 0, ...timeToRestore });
            } else if (wedding?.upgrade?.tag === 'PRIME_AND_ROYAL_CEREMONY' && record?.isPremiumAndRoyalOption) {
              list.push({ id: id, qtd: 1, cost: 0, ...timeToRestore });
            } else {
              list.push({ id: id, qtd: 1, cost: record?.cost_price ? record?.cost_price : 0, ...timeToRestore });
            }
          } else list.push({ id: id, qtd: 1, ...timeToRestore });
          displayQuantity(true);
        }

        // Auto-remove same-question options whose dependencies are now broken
        const newSelectedIds = new Set(list.map(r => r?.id ? String(r.id) : null).filter(Boolean));
        const autoRemoved = [];
        const finalList = list.filter(item => {
          const optId = item?.id ? String(item.id) : null;
          if (!optId) return true;
          const opt = question.options?.find(o => String(o._id) === optId);
          const optDeps = opt?.dependencies;
          if (!optDeps?.items?.length) return true;
          const grouped = {};
          optDeps.items.forEach(depItem => {
            const g = depItem.group ?? 0;
            if (!grouped[g]) grouped[g] = [];
            grouped[g].push(depItem);
          });
          const satisfied = Object.entries(grouped).some(([gKey, groupItems]) => {
            const gMode = optDeps.groups?.[Number(gKey)]?.mode || 'ALL';
            const evaluateItem = (depItem) => {
              if (depItem.targetQuestion) {
                const qId = String(depItem.targetQuestion?._id || depItem.targetQuestion);
                const qOptionIds = questionOptionsMap[qId];
                if (!qOptionIds) return true;
                const combined = new Set([...newSelectedIds, ...allSelectedOptionIds]);
                const anySelected = Array.from(combined).some(id => qOptionIds.has(id));
                return depItem.mustBeSelected ? anySelected : !anySelected;
              }
              const depId = String(depItem.option?._id || depItem.option);
              const isSelected = newSelectedIds.has(depId) || allSelectedOptionIds.has(depId);
              return depItem.mustBeSelected ? isSelected : !isSelected;
            };
            return gMode === 'ALL' ? groupItems.every(evaluateItem) : groupItems.some(evaluateItem);
          });
          if (!satisfied) { autoRemoved.push(opt); return false; }
          return true;
        });

        if (autoRemoved.length > 0) {
          Alert.new({ type: 'info', title: translate('DEPENDENCY_REMOVED_TITLE') || 'Opção removida', text: translate('DEPENDENCY_REMOVED_TEXT') || 'Uma opção foi removida automaticamente porque a sua dependência deixou de estar satisfeita.' });
        }

        input.onChange(autoRemoved.length > 0 ? finalList : list);
      } else {
        Alert.new({
          type: "success",
          title: translate("BLOCKED_ANSWER_TITLE"),
          text: translate("BLOCKED_ANSWER_TEXT"),
        });
      }
    }
  };

  const renderPrice = () => {
    let sufix = "",
      prefix = "";

    if (record.cost_type === CostTypes.QUANTITY) {
      sufix = ` ${translate("QUANTITY_UNI")}`;
    } else if (record.cost_type === CostTypes.TABLE) {
      sufix = ` ${translate("QUANTITY_TABLE")}`;
    } else if (record.cost_type === CostTypes.GUEST) {
      sufix = ` ${translate("QUANTITY_GUEST")}`;
    } else if (record.cost_type === CostTypes.PEOPLE) {
      sufix = ` ${translate("QUANTITY_PEOPLE")}`;
    }

    // const cost = wedding.upgrade && wedding.upgrade.hasPremiumOptions && record.isPremiumOption && record.cost
    //   ? record.costPriceExtra
    //   : record.cost_price;

    let cost = 0;
    if (selected?.cost) {
      cost = isString(selected.cost) ? parseFloat(selected.cost) : selected.cost;
    } else {
      // cost = wedding?.upgrade?.hasPremiumOptions && record?.isPremiumOption
      //   ? record?.costPriceExtra
      //   : record.cost_price;
      cost = record.cost_price;
    }

    return `${prefix}${cost}€${sufix}`;
  };

  const isRoomPlanLocationsQuestion = () => {
    if (question?._id === '67bf2952f973f10df1b6b7fa') {
      canEditSection = false;
      Alert.new({
        type: "error",
        title: translate("ROOMPLAN_BLOCKED_ADD_PLANNER")
      });
      return true;
    }

    return false;
  };

  const openImageModal = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setImageModal(true);
  };

  const closeImageModal = () => {
    setImageModal(false);
  };

  const openFullDesc = (e) => {
    e.preventDefault();
    e.stopPropagation();

    text = record.description ? record.description[activeLanguage?.code] : "";
    setFullDesc(true);
  };

  const closeFullDesc = (e) => {
    e.preventDefault();
    e.stopPropagation();

    text = record.description ? record.description[activeLanguage?.code] : "";

    if (text?.length > textLimit) {
      text = truncate(text, textLimit);
    }

    setFullDesc(false);
  };

  const showWarningPriceUpdated = (selected && selected.cost !== undefined && record && (isPremiumOption || isTrendOption || isPremiumAndRoyalOption) && parseFloat(selected.cost) > 0)
    || (selected && selected.cost !== undefined && record && !(isPremiumOption || isTrendOption || isPremiumAndRoyalOption) && parseFloat(selected.cost) !== record.cost_price)
    ? true : false;

  return (
    <React.Fragment>
      <CardContainer
        type={question.type}
        disabled={!dependencyDisabled && (disabled || disabledForCouple)}
        inModal={inModal}
        onClick={() => toggleOption(record._id)}
        style={dependencyDisabled ? { cursor: 'default' } : undefined}
      >
        <CardContent selected={selected}>
          {dependencyDisabled && (
            <DependencyDisabledOverlay>
              <Icon type="lock" />
            </DependencyDisabledOverlay>
          )}
          <SelectContainer>
            <CheckboxContainer>
              <CheckboxInput
                checkboxType="round"
                meta={{}}
                input={{ value: selected, onChange: () => null }}
              />
            </CheckboxContainer>
            <TextAvailable onlyOne={record?.onlyOneAvailable}>
              {record?.onlyOneAvailable ?
                translate("LIMITED_AVAILABILITY")
                : record.quantity
                  ? translate("VERIFY_AVAILABILITY")
                  : null}
            </TextAvailable>
          </SelectContainer>
          <BgContainer border={false} inModal={inModal}>
            {record.image?.url
              ? <ImageComponent
                zoom
                ratio={0.6}
                bgContain={record.image?.orientation === 'vertical'}
                url={record.image?.url}
                color={record.image?.color}
                hoverUrl={record?.gallery?.[0]?.url}
                hoverColor={record?.gallery?.[0]?.color}
                onClick={(e) => openImageModal(e)}
              />
              : <ImageComponent
                ratio={0.6}
                url={record.image?.url}
                color={record.image?.color}
                hoverUrl={record?.gallery?.[0]?.url}
                hoverColor={record?.gallery?.[0]?.color}
              />
            }
          </BgContainer>
          <DetailContainer>
            {((showQuantity &&
              record.cost &&
              record.cost_type === CostTypes.QUANTITY) ||
              record.cost) && (
                <PriceQuantityRow inModal={inModal}>
                  {/* {record.cost && <Price>{renderPrice()}</Price>} */}
                  {record.cost
                    ? (showWarningPriceUpdated || showUpgradeIcon)
                      ? <PriceWithWarning inModal={inModal} premium={!showWarningPriceUpdated && isPremiumOption && isTrendOption || isPremiumAndRoyalOption}>
                        <Tooltip title={
                          showWarningPriceUpdated
                            ? translate('WARNING_COST_UPDATED')
                            : [
                              isPremiumOptionOption && translate('WARNING_OPTION_WEDDING_PRIME'),
                              isPremiumAndRoyalOptionOption &&
                              translate('WARNING_OPTION_WEDDING_PRIMEANDROYAL'),
                              isTrendOptionOption && translate('WARNING_OPTION_WEDDING_TREND'),
                            ]
                              .filter(Boolean)
                              .join('\n') || null
                        }>
                          <Icon type={showWarningPriceUpdated ? 'info-circle' : 'sketch'} />
                        </Tooltip>
                        <Price inModal={inModal}>{renderPrice()}</Price>
                      </PriceWithWarning>
                      : <Price inModal={inModal}>{renderPrice()}</Price>
                    : null
                  }
                  {showQuantity &&
                    record.cost &&
                    record.cost_type === CostTypes.QUANTITY && (
                      <Quantity
                        optionID={record._id}
                        input={input}
                        isProcessClosed={isProcessClosed}
                        canEditSection={canEditSection}
                      />
                    )}
                </PriceQuantityRow>
              )}
            <Title inModal={inModal}>{record.title[activeLanguage?.code]}</Title>
          </DetailContainer>
          {selected && record.requiresTime && (
            <div
              onClick={e => e.stopPropagation()}
            >
              <div style={{ fontSize: 12, marginBottom: 4 }}>Horário</div>
              <TimePicker
                format="HH:mm"
                placeholder="Horário"
                value={selected.time ? moment(selected.time, 'HH:mm') : null}
                onChange={(_, timeString) => {
                  const list = [...input.value];
                  const idx = list.findIndex(x => x.id === record._id);
                  if (idx >= 0) {
                    list[idx] = { ...list[idx], time: timeString || undefined };
                    input.onChange(list);
                  }
                }}
              />
            </div>
          )}
          {record.description && record.description[activeLanguage?.code] && (
            <OptionTextContainer showFullDesc={showFullDesc} index={index}>
              <Text
                className="optionText"
                selected={selected}
                showFullDesc={showFullDesc}
              >
                {text}
                {record.description[activeLanguage?.code].length > textLimit &&
                  !showFullDesc && (
                    <ViewFullDesc onClick={(e) => openFullDesc(e)}>
                      &nbsp;<span>{translate("VIEW_MORE")}</span>{" "}
                      <span>
                        <ArrowLeft />
                      </span>
                    </ViewFullDesc>
                  )}
                {showFullDesc && (
                  <HideFullDescContainer>
                    <RoundButton
                      text={translate("CLOSE")}
                      onClick={closeFullDesc}
                    />
                  </HideFullDescContainer>
                )}
              </Text>
            </OptionTextContainer>
          )}
        </CardContent>
      </CardContainer>
      {showImageModal && (
        <ImageModal
          title={record.title[activeLanguage?.code]}
          image={record.image?.url}
          color={record.image?.color}
          carousel={record?.gallery?.length > 0 ? true : false}
          gallery={record?.gallery || []}
          handleCancel={closeImageModal}
        />
      )}
    </React.Fragment>
  );
};

const mapStateToProps = (state) => ({
  wedding: state.wedding.wedding,
});

export default withLocalize(connect(mapStateToProps)(OptionCard));
